/**
 * iter-338r: Active Sessions Dashboard.
 *
 * Owner/Admin view of who is using the app right now. Drives off
 * `users.last_active_at` (updated by get_current_user on every authenticated
 * request, throttled to once per 5 minutes). Auto-refreshes every 30s.
 *
 * Capabilities:
 *  - Window selector (5m / 15m / 30m / 60m / 4h / 24h).
 *  - Role distribution chips.
 *  - Online/Idle/Recent badges per user.
 *  - Revoke (force-logout) — bumps token_version server-side so all of that
 *    user's JWTs are rejected on next request.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, RefreshCw, LogOut, ShieldAlert, Circle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [5, 15, 30, 60, 240, 1440, 4320, 10080, 43200];
const DAY = 1440;
const windowLabel = (m) => (m < 60 ? `Last ${m} min` : m < DAY ? `Last ${m / 60} hr` : m === DAY ? 'Last 24 hr' : `Last ${m / DAY} Days`);
const shortWindow = (m) => (m < 60 ? `${m} min` : m < DAY ? `${m / 60} hr` : m === DAY ? '24 hr' : `${m / DAY} days`);
const agoLabel = (mins) => {
  if (mins == null) return '—';
  if (mins < 1) return '< 1m ago';
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < DAY) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / DAY)}d ago`;
};

function statusColor(s) {
  if (s === 'online') return 'text-emerald-500';
  if (s === 'idle') return 'text-amber-500';
  if (s === 'expired') return 'text-red-400';
  return 'text-stone-400';
}
function statusLabel(s) {
  return { online: 'Online', idle: 'Idle', offline: 'Offline', expired: 'Expired' }[s] || 'Recent';
}

export default function AdminActiveSessions() {
  const [data, setData] = useState({ users: [], total: 0, role_counts: {}, window_minutes: 30 });
  const [windowMin, setWindowMin] = useState(30);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/active-sessions`, {
        headers, params: { minutes: windowMin },
      });
      setData(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load sessions';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [headers, windowMin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 30000); // 30s auto-refresh
    return () => clearInterval(id);
  }, [load]);

  const revoke = async (userId, email) => {
    setRevoking(userId);
    try {
      await axios.post(`${API}/admin/active-sessions/${userId}/revoke`, {}, { headers });
      toast.success(`Force-logged out ${email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    } finally {
      setRevoking(null);
    }
  };

  const counts = data.role_counts || {};
  const roleColors = {
    owner: 'bg-violet-100 text-violet-800 border-violet-300',
    admin: 'bg-blue-100 text-blue-800 border-blue-300',
    staff: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    vendor: 'bg-amber-100 text-amber-800 border-amber-300',
    client: 'bg-pink-100 text-pink-800 border-pink-300',
    labour: 'bg-stone-100 text-stone-800 border-stone-300',
    project_manager: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="admin-active-sessions-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2 text-stone-800">
            <Activity className="text-emerald-600" size={28} />
            Active Sessions
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Live view of who&apos;s using the app. Throttled presence — refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowMin}
            onChange={(e) => setWindowMin(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 text-sm rounded-md border border-stone-300 bg-white"
            data-testid="active-sessions-window-select"
          >
            {WINDOWS.map((m) => (
              <option key={m} value={m}>
                {windowLabel(m)}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="active-sessions-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500">Total Active</div>
          <div className="text-3xl font-semibold text-stone-800 mt-1" data-testid="active-sessions-total">{data.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500">Online (&lt; 6m)</div>
          <div className="text-3xl font-semibold text-emerald-600 mt-1">
            {data.users?.filter((u) => u.status === 'online').length || 0}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500">Idle (6–30m)</div>
          <div className="text-3xl font-semibold text-amber-500 mt-1">
            {data.users?.filter((u) => u.status === 'idle').length || 0}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500">Window</div>
          <div className="text-base font-semibold text-stone-800 mt-1">
            {shortWindow(windowMin)}
          </div>
        </Card>
      </div>

      {/* Role distribution chips */}
      {Object.keys(counts).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(counts).map(([role, n]) => (
            <Badge key={role} variant="outline" className={roleColors[role] || 'bg-stone-100'}>
              {role.replace('_', ' ')}: {n}
            </Badge>
          ))}
        </div>
      )}

      {/* Users table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last seen</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(data.users || []).map((u) => (
                <tr key={u.id} className="hover:bg-stone-50/50 transition-colors" data-testid={`active-session-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.google_picture ? (
                        <img src={u.google_picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-xs font-semibold text-stone-600">
                          {(u.name || u.email || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-stone-800">{u.name || '—'}</div>
                        <div className="text-xs text-stone-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-600 text-xs uppercase tracking-wider">{u.role}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Circle size={8} className={`${statusColor(u.status)} fill-current`} />
                      <span className={`text-xs font-medium ${statusColor(u.status)}`}>{statusLabel(u.status)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-600 text-xs" title={u.last_active_at}>
                    {agoLabel(u.minutes_ago)}
                  </td>
                  <td className="px-4 py-3 text-stone-500 text-xs">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`active-session-revoke-${u.id}`} disabled={revoking === u.id}>
                          <LogOut size={13} className="mr-1" />
                          Revoke
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="text-red-600" size={20} />
                            Revoke all sessions for {u.email}?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This will force-logout the user from all devices. Their next request will be rejected and they&apos;ll have to log in again.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => revoke(u.id, u.email)}
                            data-testid={`active-session-confirm-revoke-${u.id}`}
                          >
                            Force logout
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
              {(!data.users || data.users.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-stone-500 text-sm">
                    No one has been active in the last {shortWindow(windowMin)}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
